import {
  Authority,
  TEAM_LEAD_AUTHORITY,
  useAuthenticated,
} from "providers/A12n"
import { Avatar, Badge, Collapse } from "@material-ui/core"
import { NavLink, NavLinkProps, useRouteMatch } from "react-router-dom"
import React, { ReactNode, useState } from "react"
import { css, cx } from "@emotion/css"

import { ReactComponent as AdminIcon } from "assets/nav/admin.svg"
import { ReactComponent as ArrowIcon } from "assets/nav/expand.svg"
import { ReactComponent as BillingIcon } from "assets/nav/billing.svg"
import { ReactComponent as BookingOrder } from "assets/nav/icon-b-os.svg"
import { ReactComponent as CampaignsIcon } from "assets/nav/project.svg"
import { ReactComponent as DashboardIcon } from "assets/nav/dashboard.svg"
import ExpandMoreIcon from "@material-ui/icons/ExpandMore"
import IconButton from "@material-ui/core/IconButton"
import { ReactComponent as LogoLarge } from "assets/logos/logo-halogen-white.svg"
import { ReactComponent as LogoSmall } from "assets/logos/logo-halogen-white-small.svg"
import { ReactComponent as NotificationsIcon } from "assets/icon-bell.svg"
import { ReactComponent as PlansIcon } from "assets/nav/timeline.svg"
import { ReactComponent as Question } from "assets/nav/icon-question.svg"
import { ReactComponent as ReportingIcon } from "assets/nav/reporting.svg"
import { ReactComponent as UserGuide } from "assets/nav/icon-user-guide.svg"
import { ReactComponent as SupportIcon } from "assets/nav/clients.svg"
import { WithAuthority } from "components/WithAuthority"
import { fetchNotifications } from "api/notifications"
import { styles as normalStyles } from "./styles"
import { useI18n } from "providers/I18n"
import { useLocation } from "react-router-dom"
import { useQuery } from "react-query"

let styles = normalStyles

const revenueDashboardExtraStyles = {
  root: css`
    background-color: var(--primary-main-green);
  `,
  link: css`
    :hover {
      background-color: var(--primary-main-dark-green);
    }
  `,
  links: css`
    .linkIcon {
      :hover {
        background-color: var(--primary-main-dark-green);
      }
    }
  `,
  navLink: css`
    :hover {
      background-color: var(--primary-main-dark-green);
    }
  `,
  expandedActiveNavLink: css`
    .active {
      background-color: var(--primary-main-dark-green);
    }
    &:hover {
      background-color: var(--primary-main-dark-green);
    }
  `,
  navItem: css`
    :hover {
      > div {
        background-color: var(--primary-main-light-green);
      }
    }
  `,
  activeNavLink: css`
    background-color: var(--primary-main-dark-green);
  `,
}
const revenueDashboardStyles = {
  ...normalStyles,
  root: cx(normalStyles.root, revenueDashboardExtraStyles.root),
  link: cx(normalStyles.link, revenueDashboardExtraStyles.link),
  links: cx(normalStyles.links, revenueDashboardExtraStyles.links),
  navLink: cx(normalStyles.navLink, revenueDashboardExtraStyles.navLink),
  navItem: cx(normalStyles.navItem, revenueDashboardExtraStyles.navItem),
  expandedActiveNavLink: cx(
    normalStyles.expandedActiveNavLink,
    revenueDashboardExtraStyles.expandedActiveNavLink,
  ),
  activeNavLink: cx(
    normalStyles.activeNavLink,
    revenueDashboardExtraStyles.activeNavLink,
  ),
}

type ItemProps = {
  link: NavLinkConfig
  isCollapsed: boolean
  expanded?: string | null
  handleExpand: (
    evt: React.MouseEvent<
      HTMLDivElement | HTMLAnchorElement | HTMLLIElement,
      MouseEvent
    >,
  ) => void
}

const Item = (props: ItemProps) => {
  const { link, isCollapsed, expanded, handleExpand } = props
  const match = useRouteMatch({ path: link.match, exact: link.exact })
  const location = useLocation()
  const to = link.to === "#" ? location : link.to

  return (
    <WithAuthority allowed={link.allowed}>
      <li className={link.className}>
        {link.sublinks?.length && !isCollapsed ? (
          <>
            <div
              className={cx(styles.navLink, {
                [styles.activeNavLink]: !!match,
              })}
              onClick={handleExpand}
              aria-expanded={expanded === link.label}
              data-name={link.label}
              data-test-id="campaign-select"
            >
              <span className="icon">{link.icon}</span>
              <span className={cx("BodyBody-1WhiteRegular", "label")}>
                {link.label}
              </span>
              {link.sublinks?.length ? (
                <IconButton
                  className={cx(styles.collapseExpandIcon, {
                    [styles.collapseExpandedIcon]: expanded === link.label,
                  })}
                >
                  <ExpandMoreIcon />
                </IconButton>
              ) : null}
            </div>
            <Collapse
              in={expanded === link.label}
              classes={{ wrapperInner: styles.wrapper }}
            >
              {link.sublinks.map((sl: NavLinkCommon, sldx) => {
                return (
                  <WithAuthority allowed={sl.allowed} key={sldx}>
                    <SubItem link={sl} />
                  </WithAuthority>
                )
              })}
            </Collapse>
          </>
        ) : (
          <NavLink
            to={to}
            exact={link.exact}
            className={cx(styles.navLink)}
            activeClassName={cx({ [styles.activeNavLink]: !!match })}
            onClick={isCollapsed ? link.onClick : link.onClickExpand}
            data-name={link.label}
          >
            <span className="icon">{link.icon}</span>
            {isCollapsed ? null : (
              <>
                <span
                  className={cx("BodyBody-1WhiteRegular", "label")}
                  data-test-id={`nav-menu-item-${link.label}`}
                >
                  {link.label}
                </span>
                {link.sublinks?.length ? (
                  <IconButton
                    className={cx(styles.collapseExpandIcon, {
                      [styles.collapseExpandedIcon]: expanded === link.label,
                    })}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                ) : null}
              </>
            )}
          </NavLink>
        )}
      </li>
    </WithAuthority>
  )
}

const SubItem = (props: { link: NavLinkCommon }) => {
  const { link } = props
  const match = useRouteMatch({ path: link.match, exact: link.exact })

  const location = useLocation()
  if (location.pathname === "/revenue") {
    styles = revenueDashboardStyles
  } else {
    styles = normalStyles
  }

  return (
    <div
      className={cx(styles.navItem, {
        [styles.activeNavLink]: !!match,
      })}
    >
      <div
        className={cx(styles.sublinkBorderDiv, {
          active: !!match,
        })}
      />
      <NavLink
        to={link.to}
        className={cx(
          styles.navLink,
          {
            active: !!match,
          },
          "sublink",
        )}
        onClick={link.onClick}
      >
        <span className={cx("BodyBody-1WhiteRegular")}>{link.label}</span>
      </NavLink>
    </div>
  )
}

const ps = new URLSearchParams([["limit", "15"]]).toString()

type NavLinkCommon = {
  label: string
  icon?: JSX.Element
  to: string
  match: string[]
  allowed?: Authority[]
  onClickExpand?: (
    evt: React.MouseEvent<
      HTMLDivElement | HTMLAnchorElement | HTMLLIElement,
      MouseEvent
    >,
  ) => void
} & NavLinkProps

type NavLinkConfig = NavLinkCommon & {
  sublinks?: NavLinkCommon[]
}

type NavProps = {
  isCollapsed: boolean
  onToggle: () => void
}

function Nav({ isCollapsed, onToggle }: NavProps) {
  const { t } = useI18n()

  const auth = useAuthenticated()
  const { user, meta } = auth
  const { family_name = "", given_name = "" } = user ?? {}
  const { userRole } = meta ?? {}

  const initials = `${given_name[0]}${family_name[0]}`

  const q = useQuery(["notifications", ps], fetchNotifications)
  const allRead = (q.data?.totalUnread ?? 0) === 0

  const [expanded, setExpanded] = useState<string | null>()

  const location = useLocation()
  if (location.pathname === "/revenue") {
    styles = revenueDashboardStyles
  } else {
    styles = normalStyles
  }

  const handleExpand = (
    evt: React.MouseEvent<
      HTMLDivElement | HTMLAnchorElement | HTMLLIElement,
      MouseEvent
    >,
  ) => {
    const name = evt.currentTarget.dataset.name
    if (!name) return
    setExpanded((prev) => (prev === name ? null : name))
  }

  const links: Array<NavLinkConfig> = [
    {
      label: t("nav.dashboard"),
      to: "#",
      match: [
        "/",
        // "/planning-dashboard",
        // "/buying-billing-dashboard",
        "/regional-dashboard",
        "/revenue",
      ],
      icon: <DashboardIcon />,
      exact: true,
      allowed: [
        "AUTH_VIEW_REVENUE_DASHBOARD",
        "AUTH_ACCESS_GLOBAL_FINANCE",
        "AUTH_ACCESS_GLOBAL_ADMIN",
        "AUTH_ACCESS_GLOBAL_FINANCE",
        "AUTH_ACCESS_REGION_FINANCE",
        "AUTH_VIEW_EXECUTIVE_DASHBOARD",
      ],
      sublinks: [
        // {
        //   label: t("nav.planning-dashboard"),
        //   match: ["/planning-dashboard"],
        //   to: "/planning-dashboard",
        //   allowed: [
        //     "AUTH_ACCESS_GLOBAL_ADMIN",
        //     "AUTH_ACCESS_GLOBAL_FINANCE",
        //     "AUTH_ACCESS_MARKET_ADMIN",
        //     "AUTH_ACCESS_MARKET_FINANCE",
        //     "AUTH_ACCESS_AGENCY_ADMIN",
        //     "AUTH_ACCESS_AGENCY_FINANCE",
        //     "AUTH_ACCESS_REGION_FINANCE",
        //     "AUTH_ACCESS_PLAN",
        //     "AUTH_ACCESS_BUYER",
        //     "AUTH_VIEW_PLANNING_DASHBOARD",
        //   ],
        // },
        {
          label: t("nav.revenue"),
          match: ["/revenue"],
          to: "/revenue",
          allowed: [
            "AUTH_VIEW_REVENUE_DASHBOARD",
            "AUTH_ACCESS_GLOBAL_FINANCE",
          ],
        },
        // {
        //   label: t("nav.buying-billing-dashboard"),
        //   match: ["/buying-billing-dashboard"],
        //   to: "/buying-billing-dashboard",
        //   allowed: [
        //     "AUTH_ACCESS_GLOBAL_ADMIN",
        //     "AUTH_ACCESS_GLOBAL_FINANCE",
        //     "AUTH_ACCESS_MARKET_ADMIN",
        //     "AUTH_ACCESS_MARKET_FINANCE",
        //     "AUTH_ACCESS_AGENCY_FINANCE",
        //     "AUTH_ACCESS_REGION_FINANCE",
        //     "AUTH_VIEW_BUYING_BILLING_DASHBOARD",
        //   ],
        // },
        {
          label: t("nav.regional-dashboard"),
          match: ["/regional-dashboard"],
          to: "/regional-dashboard",
          allowed: [
            "AUTH_ACCESS_GLOBAL_ADMIN",
            "AUTH_ACCESS_GLOBAL_FINANCE",
            "AUTH_ACCESS_REGION_FINANCE",
            "AUTH_VIEW_EXECUTIVE_DASHBOARD",
          ],
        },
      ],
      onClick: onToggle,
    },
    {
      label: t("nav.campaigns"),
      match: ["/campaigns"],
      to: "/campaigns",
      icon: <CampaignsIcon />,
      allowed: ["AUTH_VIEW_LISTING_CAMPAIGN"],
      onClickExpand: handleExpand,
    },
    {
      label: t("nav.plans"),
      match: ["/plans"],
      to: "/plans",
      icon: <PlansIcon />,
      allowed: ["AUTH_VIEW_LISTING_PLAN"],
      onClickExpand: handleExpand,
    },
    {
      label: t("nav.bookingOrders"),
      match: ["/booking-orders"],
      to: "/booking-orders",
      icon: <BookingOrder className={styles.largeIcon} />,
      allowed: ["AUTH_VIEW_BOOKING_ORDER"],
      onClickExpand: handleExpand,
    },
    {
      label: t("nav.reports"),
      match: ["/reporting"],
      to: "/reporting",
      icon: <ReportingIcon className={styles.largeIcon} />,
      allowed: ["AUTH_VIEW_REPORT_BUILDER"],
      onClickExpand: handleExpand,
    },
    {
      label: t("nav.billing"),
      match: ["/invoices"],
      to: "/invoices",
      icon: <BillingIcon />,
      allowed: ["AUTH_ACCESS_BILLER"],
      onClickExpand: handleExpand,
    },
    {
      label: t("nav.support"),
      to: "#",
      match: ["/support/*"],
      icon: <SupportIcon />,
      allowed: ["AUTH_ACCESS_SUPPORT"],
      sublinks: [
        {
          label: t("nav.support.market_initialization"),
          match: ["/support/market-initialization"],
          to: "/support/market-initialization",
          allowed: ["AUTH_ACCESS_SUPPORT"],
        },
        {
          label: t("nav.support.user_management"),
          match: ["/support/user-management"],
          to: "/support/user-management",
          allowed: ["AUTH_ACCESS_SUPPORT"],
        },
      ],
      onClick: onToggle,
    },

    {
      label: t("nav.admin"),
      to: "#",
      match: ["/admin-*", "/market-*"],
      icon: <AdminIcon className={styles.largeIcon} />,
      allowed: [
        "AUTH_ACCESS_GLOBAL_ADMIN",
        "AUTH_ACCESS_GLOBAL_FINANCE",
        "AUTH_ACCESS_AGENCY_ADMIN",
        "AUTH_ACCESS_AGENCY_FINANCE",
        "AUTH_ACCESS_REGION_FINANCE",
        "AUTH_MANAGE_USER_GLOBAL",

        /** market permissions */
        "AUTH_ACCESS_MARKET_ADMIN",
        "AUTH_ACCESS_MARKET_FINANCE",

        /** team leader permission */
        TEAM_LEAD_AUTHORITY,
      ],
      sublinks: [
        /** Global Admin */
        {
          label: t("nav.admin.vendorgroups"),
          match: ["/admin-supplier-groups"],
          to: "/admin-supplier-groups",
          allowed: ["AUTH_ACCESS_GLOBAL_ADMIN", "AUTH_ACCESS_GLOBAL_FINANCE"],
        },
        {
          label: t("nav.admin.clientgroups"),
          match: ["/admin-client-groups"],
          to: "/admin-client-groups",
          allowed: ["AUTH_ACCESS_GLOBAL_ADMIN", "AUTH_ACCESS_GLOBAL_FINANCE"],
        },
        {
          label: t("nav.admin.media-types"),
          match: ["/admin-mediaTypes"],
          to: "/admin-mediaTypes",
          allowed: ["AUTH_ACCESS_GLOBAL_ADMIN", "AUTH_ACCESS_GLOBAL_FINANCE"],
        },
        {
          label: t("nav.admin.media-fields"),
          match: ["/admin-mediaFields"],
          to: "/admin-mediaFields",
          allowed: ["AUTH_ACCESS_GLOBAL_ADMIN", "AUTH_ACCESS_GLOBAL_FINANCE"],
        },
        {
          label: t("nav.admin.regions"),
          match: ["/admin-regions"],
          to: "/admin-regions/APAC",
          allowed: [
            "AUTH_ACCESS_GLOBAL_ADMIN",
            "AUTH_ACCESS_GLOBAL_FINANCE",
            "AUTH_ACCESS_REGION_FINANCE",
          ],
        },
        {
          label: t("nav.labels"),
          match: ["/admin-labels"],
          to: "/admin-labels",
          allowed: ["AUTH_MANAGE_USER_GLOBAL"],
        },
        /** Market Admin */
        {
          label: t("nav.market.settings"),
          match: ["/market-settings"],
          to: "/market-settings",
          allowed: ["AUTH_ACCESS_MARKET_ADMIN", "AUTH_ACCESS_MARKET_FINANCE"],
        },
        {
          label: t("nav.market.companies"),
          match: ["/market-companies"],
          to: "/market-companies",
          allowed: ["AUTH_ACCESS_MARKET_ADMIN", "AUTH_ACCESS_MARKET_FINANCE"],
        },
        {
          label: t("nav.market.clients"),
          match: ["/market-clients"],
          to: "/market-clients",
          allowed: ["AUTH_ACCESS_MARKET_ADMIN", "AUTH_ACCESS_MARKET_FINANCE"],
        },
        {
          label: t("nav.market.agencies"),
          match: ["/market-agencies"],
          to: "/market-agencies",
          allowed: ["AUTH_ACCESS_MARKET_ADMIN", "AUTH_ACCESS_MARKET_FINANCE"],
        },
        {
          label: t("nav.market.mediatypes"),
          match: ["/market-mediatypes"],
          to: "/market-mediatypes",
          allowed: ["AUTH_ACCESS_MARKET_ADMIN", "AUTH_ACCESS_MARKET_FINANCE"],
        },
        {
          label: t("nav.market.mediafields"),
          match: ["/market-mediafields"],
          to: "/market-mediafields",
          allowed: ["AUTH_ACCESS_MARKET_ADMIN", "AUTH_ACCESS_MARKET_FINANCE"],
        },
        {
          label: t("nav.market.vendors"),
          match: ["/market-vendors"],
          to: "/market-vendors",
          allowed: ["AUTH_ACCESS_MARKET_ADMIN", "AUTH_ACCESS_MARKET_FINANCE"],
        },
        {
          label: t("nav.market.taxes"),
          match: ["/market-taxes"],
          to: "/market-taxes",
          allowed: ["AUTH_ACCESS_MARKET_ADMIN", "AUTH_ACCESS_MARKET_FINANCE"],
        },
        {
          label: t("nav.market.team"),
          match: ["/market-teams"],
          to: "/market-teams",
          allowed: ["AUTH_ACCESS_MARKET_ADMIN", "AUTH_ACCESS_MARKET_FINANCE"],
        },
        {
          label: t("nav.market.targets"),
          match: ["/market-targets"],
          to: "/market-targets/targets",
          allowed: ["AUTH_ACCESS_MARKET_ADMIN", "AUTH_ACCESS_MARKET_FINANCE"],
        },

        /** Agency Admin */
        {
          label: t("nav.agency.companies"),
          match: ["/agency-companies"],
          to: "/agency-companies",
          allowed: ["AUTH_ACCESS_AGENCY_ADMIN", "AUTH_ACCESS_AGENCY_FINANCE"],
        },
        {
          label: t("nav.agency.clients"),
          match: ["/agency-clients"],
          to: "/agency-clients",
          allowed: [
            "AUTH_ACCESS_AGENCY_ADMIN",
            "AUTH_ACCESS_AGENCY_FINANCE",
            TEAM_LEAD_AUTHORITY,
          ],
        },
        {
          label: t("nav.agency.mediatypes"),
          match: ["/agency-mediatypes"],
          to: "/agency-mediatypes",
          allowed: ["AUTH_ACCESS_AGENCY_ADMIN", "AUTH_ACCESS_AGENCY_FINANCE"],
        },
        {
          label: t("nav.agency.vendors"),
          match: ["/agency-vendors"],
          to: "/agency-vendors",
          allowed: ["AUTH_ACCESS_AGENCY_ADMIN", "AUTH_ACCESS_AGENCY_FINANCE"],
        },
        {
          label: t("nav.agency.teams"),
          match: ["/agency-teams"],
          to: "/agency-teams",
          allowed: ["AUTH_ACCESS_AGENCY_ADMIN", "AUTH_ACCESS_AGENCY_FINANCE"],
        },
        {
          label: t("nav.agency.documents"),
          match: ["/agency-documents"],
          to: "/agency-documents",
          allowed: ["AUTH_ACCESS_AGENCY_FINANCE"],
        },
      ],
      onClick: onToggle,
    },
  ]

  const totalUnread = q?.data?.totalUnread ?? 0
  const max = 99
  const totalUnreadDisplayed = totalUnread > max ? `${max}+` : totalUnread
  return (
    <div
      className={cx(
        styles.root,
        // isRevenueDashboard && revenueDashboardStyle.root,
      )}
    >
      {!isCollapsed ? (
        <div className={styles.logo}>
          <LogoLarge />
        </div>
      ) : (
        <div className={styles.logo}>
          <LogoSmall />
        </div>
      )}
      <ul role="navigation" className={styles.navList}>
        <li
          className={cx(styles.toggleButton, {
            [styles.mirrored]: !isCollapsed,
          })}
          onClick={onToggle}
          data-testid={isCollapsed ? "nav-menu-collapsed" : "nav-menu-expanded"}
        >
          <ArrowIcon />
        </li>

        {links.map((link: NavLinkConfig, ldx) => (
          <Item
            link={link}
            isCollapsed={isCollapsed}
            key={ldx}
            expanded={expanded}
            handleExpand={handleExpand}
          />
        ))}

        <li>
          <NotificationsLink
            matchPath={["/notifications"]}
            handleExpand={handleExpand}
            label={t("nav.notifications")}
          >
            {isCollapsed ? (
              <span>
                <Badge
                  color="secondary"
                  invisible={allRead}
                  badgeContent={q?.data?.totalUnread}
                  className={styles.badge}
                >
                  <NotificationsIcon className={styles.largeIcon} />
                </Badge>
              </span>
            ) : (
              <span>
                <NotificationsIcon className={styles.largeIcon} />
              </span>
            )}

            {isCollapsed ? null : (
              <>
                <span
                  className={cx(
                    "BodyBody-1WhiteRegular",
                    styles.notificationsLabel,
                  )}
                >
                  {t("nav.notifications")}
                  <span className={cx("BodyBody-sWhiteBold", styles.unread)}>
                    {totalUnreadDisplayed}
                  </span>
                </span>
              </>
            )}
          </NotificationsLink>
        </li>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Links */}
        <li
          className={cx(styles.links, {
            [styles.collapsedLinks]: !isCollapsed,
          })}
          onClick={handleExpand}
          data-name={"help"}
        >
          {isCollapsed ? (
            <a
              rel="noreferrer"
              href="/user-guide/faq/general"
              target="_blank"
              className="linkIcon"
            >
              <Question />
            </a>
          ) : (
            <a
              rel="noreferrer"
              href="/user-guide/faq/general"
              target="_blank"
              className={cx("BodyBody-sWhiteRegular", styles.link)}
            >
              {t("sidebar.link.FAQ")}
            </a>
          )}
          {isCollapsed ? (
            <a
              rel="noreferrer"
              href="/user-guide/"
              target="_blank"
              className="linkIcon"
            >
              <UserGuide />
            </a>
          ) : (
            <a
              rel="noreferrer"
              href="/user-guide/"
              target="_blank"
              className={cx("BodyBody-sWhiteRegular", styles.link)}
            >
              {t("sidebar.link.user.guide")}
            </a>
          )}
        </li>

        {/* Account */}
        <li className={styles.navListAccount}>
          {!isCollapsed ? (
            <>
              <div
                className={cx(styles.navLink, {
                  [styles.activeNavLink]: expanded === "account",
                })}
                onClick={handleExpand}
                data-name="account"
              >
                <span className={styles.navItemAvatar}>
                  <Avatar
                    children={initials}
                    className={cx("BodyBody-sWhiteRegular", styles.avatar)}
                  />
                </span>
                <span
                  className={cx(
                    "BodyBody-2WhiteRegular",
                    "label",
                    styles.accountDiv,
                  )}
                >
                  <span
                    className={styles.accountName}
                  >{`${given_name} ${family_name}`}</span>
                  <span
                    className={cx("BodyBody-sWhiteRegular", styles.accountName)}
                  >
                    {t(`role.${userRole}`)}
                  </span>
                </span>
                <IconButton
                  className={cx(styles.collapseExpandIcon, {
                    [styles.collapseExpandedIcon]: expanded !== "account",
                  })}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </div>
              <Collapse
                in={expanded === "account"}
                classes={{ wrapperInner: styles.wrapper }}
              >
                <div className={styles.navItem}>
                  <div className={styles.sublinkBorderDiv} />
                  <NavLink
                    to="#"
                    className={cx(styles.navLink, "sublink")}
                    onClick={auth.logout}
                  >
                    <span className={cx("BodyBody-1WhiteRegular")}>
                      {t("account.logout")}
                    </span>
                  </NavLink>
                </div>
              </Collapse>
            </>
          ) : (
            <div
              className={cx(styles.navLink)}
              onClick={onToggle}
              data-name="account"
            >
              <span className={styles.navItemAvatar}>
                <Avatar
                  children={initials}
                  className={cx("BodyBody-sWhiteRegular", styles.avatar)}
                />
              </span>
            </div>
          )}
        </li>
      </ul>
    </div>
  )
}

export default Nav

type NotificationsLinkProps = {
  children: ReactNode
  matchPath: string[]
  handleExpand: (
    evt: React.MouseEvent<
      HTMLDivElement | HTMLAnchorElement | HTMLLIElement,
      MouseEvent
    >,
  ) => void
  label: string
}

const NotificationsLink = (props: NotificationsLinkProps) => {
  const { children, matchPath, handleExpand, label } = props
  const match = useRouteMatch({ path: matchPath })

  const location = useLocation()
  if (location.pathname === "/revenue") {
    styles = revenueDashboardStyles
  } else {
    styles = normalStyles
  }

  return (
    <NavLink
      to="/notifications"
      className={cx(styles.navLink)}
      activeClassName={cx({ [styles.activeNavLink]: !!match })}
      onClick={handleExpand}
      data-name={label}
    >
      {children}
    </NavLink>
  )
}
